import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useApi } from '../api/api';
import { useMap } from '../features/mapping/MapContext';
import type { PageLayoutResponse, LayoutPanel, LayoutComponent } from '../types/layout';

export type PageType = 'home' | 'region' | 'plan' | 'stock';

export interface UsePageLayoutOptions {
  pageType: PageType;
  resourceId: number | null;
}

export interface UsePageLayoutResult {
  layout: PageLayoutResponse | null;
  loading: boolean;
  error: Error | null;
  /** Panel sections keyed by panel slug */
  leftPanel: LayoutPanel | null;
  rightPanel: LayoutPanel | null;
  bottomPanel: LayoutPanel | null;
  /** Components from bottom panel's active-data-layers section (for DataLayerTabs) */
  bottomPanelInitialLayers: Record<string, Record<string, unknown> | null> | null;
}

/**
 * Normalizes API camera (snake_case) to Cesium format.
 * Handles both camera_lat/camera_lon/camera_height_m and latitude/longitude/height.
 */
function normalizeCamera(
  camera: PageLayoutResponse['camera']
): { longitude: number; latitude: number; height: number; heading: number; pitch: number; roll: number } | null {
  if (!camera) return null;
  const lat = (camera as Record<string, number>).camera_lat ?? (camera as Record<string, number>).latitude;
  const lon = (camera as Record<string, number>).camera_lon ?? (camera as Record<string, number>).longitude;
  const height = (camera as Record<string, number>).camera_height_m ?? (camera as Record<string, number>).height;
  const heading = (camera as Record<string, number>).camera_heading ?? (camera as Record<string, number>).heading ?? 0;
  const pitch = (camera as Record<string, number>).camera_pitch ?? (camera as Record<string, number>).pitch ?? 0;
  const roll = (camera as Record<string, number>).camera_roll ?? (camera as Record<string, number>).roll ?? 0;
  if (lat == null || lon == null || height == null) return null;
  return { longitude: lon, latitude: lat, height, heading, pitch, roll };
}

/** Normalize slug to kebab-case (e.g. vessel_tracking → vessel-tracking) for MapController/registry. */
function toKebabSlug(slug: string): string {
  return String(slug ?? '').replace(/_/g, '-');
}

/**
 * Converts layout components to bottomPanelInitialLayers format expected by DataLayerTabs.
 * All components in the section are included; slug is normalized to kebab-case for MapController.
 */
function componentsToInitialLayers(components: LayoutComponent[] | undefined): Record<string, Record<string, unknown> | null> | null {
  if (!components || components.length === 0) return null;
  const layers: Record<string, Record<string, unknown> | null> = {};
  for (const comp of components) {
    const slug = toKebabSlug(comp.component_slug);
    if (slug) layers[slug] = comp.component_default_values;
  }
  return layers;
}

export function usePageLayout({ pageType, resourceId }: UsePageLayoutOptions): UsePageLayoutResult {
  const { getPageLayout } = useApi();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { setCesiumView, viewerReady, viewer } = useMap() as {
    setCesiumView: (config: object) => void;
    viewerReady: boolean;
    viewer: unknown;
  };
  const [layout, setLayout] = useState<PageLayoutResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const panels = layout?.panels ?? [];
  const leftPanel = panels.find((p) => p.panel_slug === 'left-panel') ?? null;
  const rightPanel = panels.find((p) => p.panel_slug === 'right-panel') ?? null;
  const bottomPanel =
    panels.find((p) => p.panel_slug === 'bottom-panel') ??
    panels.find((p) => (p as { panel_slug?: string }).panel_slug === 'bottom_panel') ??
    null;
  const bottomPanelLayersSection =
    bottomPanel?.sections?.find((s) => s.section_slug === 'active-data-layers') ??
    bottomPanel?.sections?.find((s) => (s as { section_slug?: string }).section_slug === 'active_data_layers') ??
    undefined;
  const bottomPanelInitialLayers = componentsToInitialLayers(bottomPanelLayersSection?.components) ?? null;

  // Fetch layout when resourceId is provided (or when pageType is 'home'—API allows home without resource_id)
  useEffect(() => {
    if (authLoading) {
      setLayout(null);
      setLoading(true);
      setError(null);
      return;
    }

    if (!isAuthenticated) {
      setLayout(null);
      setLoading(false);
      setError(null);
      return;
    }

    if (resourceId == null && pageType !== 'home') {
      setLayout(null);
      setLoading(true);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchLayout = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getPageLayout(pageType, resourceId ?? undefined);
        const data = response?.data as PageLayoutResponse | undefined;
        if (!cancelled && data) {
          setLayout(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLayout(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLayout();
    return () => {
      cancelled = true;
    };
  }, [pageType, resourceId, getPageLayout, authLoading, isAuthenticated]);

  // Apply camera when layout has camera and viewer is ready
  useEffect(() => {
    const Cesium = typeof window !== 'undefined' ? (window as { Cesium?: typeof import('cesium') }).Cesium : null;
    if (!viewerReady || !viewer || !Cesium) return;

    const layoutCamera = normalizeCamera(layout?.camera);
    if (!layoutCamera) return;

    const camera = layoutCamera;

    // Backend sends orientation in radians
    const toRad = (deg: number) => deg; // layout API sends radians
    const headingRad = toRad(camera.heading);
    let pitchRad = toRad(camera.pitch);
    const rollRad = toRad(camera.roll);

    // Cesium expects negative pitch for looking down; layout API sends +π/2 for top-down
    if (pitchRad > 0) {
      pitchRad = -pitchRad;
    }

    setCesiumView({
      mode: 'custom',
      camera: {
        destination: Cesium.Cartesian3.fromDegrees(
          camera.longitude,
          camera.latitude,
          camera.height
        ),
        orientation: {
          heading: headingRad,
          pitch: pitchRad,
          roll: rollRad,
        },
      },
      interactive: true,
      duration: 1.5,
    });

    const timeoutId = setTimeout(() => {
      const v = viewer as { scene?: { screenSpaceCameraController?: Record<string, boolean> } } | null;
      if (Cesium && viewerReady && v?.scene?.screenSpaceCameraController) {
        const ctrl = v.scene.screenSpaceCameraController;
        ctrl.enableRotate = true;
        ctrl.enableTranslate = true;
        ctrl.enableZoom = true;
        ctrl.enableTilt = true;
        ctrl.enableLook = true;
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [resourceId, layout?.camera, setCesiumView, viewerReady, viewer]);

  return {
    layout,
    loading,
    error,
    leftPanel,
    rightPanel,
    bottomPanel,
    bottomPanelInitialLayers,
  };
}
